'use client';

import { useEffect, useState } from 'react';

import { TrackedButton } from '@/components';
import { AuthMachineEvent } from '@/machines';
import { logger } from '@/utils';

import styles from './AuthOtpResendButton.module.css';

interface AuthOtpResendButtonProps {
  onSuccess?: () => void;
  onError?: (message: string) => void;
  disabled?: boolean;
  cooldownSeconds?: number;
  phoneNumber: string;
  state: string;
  send: (event: AuthMachineEvent) => void;
}

const AuthOtpResendButton = ({
  onSuccess,
  disabled = false,
  cooldownSeconds = 30,
  phoneNumber,
  send,
}: AuthOtpResendButtonProps) => {
  const [countdown, setCountdown] = useState(0);

  // Start countdown on first render
  useEffect(() => {
    let timerId: NodeJS.Timeout;

    if (countdown > 0) {
      timerId = setInterval(() => {
        setCountdown((c) => Math.max(0, c - 1));
      }, 1000);
    }

    return () => {
      if (timerId) clearInterval(timerId);
    };
  }, [countdown]);

  // We no longer need to differentiate between registration and login flow
  // as the state machine handles this automatically based on the isNewUser context

  const handleSendOtp = () => {
    logger.debug(`Requesting to resend OTP for ${phoneNumber}`);
    setCountdown(cooldownSeconds);

    // Use RETRY event to trigger the OTP sending flow again
    // This will transition back to sendingLoginOTP or sendingRegistrationOTP state
    // which will directly call the appropriate OTP sending action
    send({ type: 'RETRY' });

    if (onSuccess) onSuccess();
  };

  return (
    <TrackedButton
      variant="subtle"
      onClick={handleSendOtp}
      disabled={disabled || countdown > 0}
      className={styles.resendButton}
      trackingName="resend_otp"
      trackingProperties={{
        countdown: countdown,
      }}
    >
      {countdown > 0 ? `Resend Code (${countdown}s)` : 'Resend Code'}
    </TrackedButton>
  );
};

export { AuthOtpResendButton };
